import closeIcon from '@iconify/icons-material-symbols/close';
import descriptionIcon from '@iconify/icons-material-symbols/description-outline';
import spinnerIcon from '@iconify/icons-material-symbols/progress-activity';
import { useEffect, useState } from 'react';
import { formatBytes } from '#pie/utils/files';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { Icon } from '../icon/Icon';

export interface AttachmentCardProps {
  className?: string;
  file: File;
  onDelete?: () => void;
  onClick?: () => void;
  isLoading?: boolean;
  errorMessage?: string;
}

function getExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop() ?? '').toUpperCase() : '';
}

function isPreviewableImage(file: File): boolean {
  return file.type.startsWith('image/');
}

export const AttachmentCard = ({
  className,
  file,
  onDelete,
  onClick,
  isLoading = false,
  errorMessage,
}: AttachmentCardProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isPreviewableImage(file)) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const s = attachmentCard({ hasError: !!errorMessage });
  const extension = getExtension(file.name);

  return (
    <div className={cn(s.base(), className)}>
      <button
        type="button"
        className={s.row()}
        onClick={onClick}
        disabled={isLoading || !onClick}
      >
        <div className={s.thumb()}>
          {isLoading ? (
            <Icon
              role="progressbar"
              aria-label="loading"
              icon={spinnerIcon}
              size={16}
              className="animate-spin text-content-tertiary"
            />
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <Icon
              icon={descriptionIcon}
              size={20}
              className="text-content-tertiary"
            />
          )}
        </div>

        <div className={s.meta()}>
          <span className={s.fileName()} title={file.name}>
            {file.name}
          </span>
          <span className={s.fileInfo()}>
            {isLoading
              ? 'Uploading…'
              : `${extension}${extension ? ' · ' : ''}${formatBytes(file.size)}`}
          </span>
        </div>
      </button>

      {onDelete && (
        <button
          type="button"
          aria-label="Delete file"
          className={s.deleteButton()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
        >
          <Icon icon={closeIcon} size={16} className="text-content-tertiary" />
        </button>
      )}

      {errorMessage && <span className={s.errorText()}>{errorMessage}</span>}
    </div>
  );
};

const attachmentCard = tv({
  slots: {
    base: 'flex w-full items-center gap-1 rounded-card border border-border-disabled bg-surface-primary px-3 py-2 hover:shadow-card',
    row: 'flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default',
    thumb:
      'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-surface-disabled',
    meta: 'flex min-w-0 flex-1 flex-col',
    fileName: 'truncate text-sm text-content-primary',
    fileInfo: 'text-xs text-content-tertiary',
    deleteButton:
      'ml-auto flex h-6 w-6 shrink-0 items-center justify-center self-center',
    errorText: 'text-xs text-content-error',
  },
  variants: {
    hasError: {
      true: { base: 'border-support-error-bold' },
    },
  },
});
